import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { idpApi } from '../../services/idp-api';

export function ForgotPasswordPage() {
  const { t } = useTranslation('idp');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isFormInvalid = useMemo(() => email.trim().length === 0, [email]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await idpApi.forgotPassword({ email: email.trim() });
      setSubmitted(true);
    } catch {
      setErrorMessage(t('forgotPassword.errors.default'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 520 }}>
        <CardContent>
          <Stack
            spacing={3}
            component="form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
          >
            <Stack spacing={1}>
              <Typography variant="h5" component="h1" fontWeight={700}>
                {t('forgotPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('forgotPassword.subtitle')}
              </Typography>
            </Stack>

            {submitted ? (
              <Alert severity="success">{t('forgotPassword.successMessage')}</Alert>
            ) : null}
            {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

            <TextField
              label={t('forgotPassword.emailLabel')}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              required
              fullWidth
            />

            <Button type="submit" variant="contained" disabled={isSubmitting || isFormInvalid}>
              {isSubmitting
                ? t('forgotPassword.submittingButton')
                : t('forgotPassword.submitButton')}
            </Button>

            <Typography variant="body2" color="text.secondary">
              {t('forgotPassword.backToSignIn')}{' '}
              <Link component={RouterLink} to="/auth/signin" underline="hover">
                {t('forgotPassword.signInLink')}
              </Link>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
